"use client";

import { cn } from "@/lib/utils";

type Testimonial = {
  company: string;
  quote: string;
  person: string;
  role: string;
};

const testimonials: Testimonial[] = [
  {
    company: "AERONÁUTICA IBÉRICA",
    quote:
      "Redujimos el tiempo de análisis de datos de vuelo de 2 semanas a 4 horas. La plataforma nos permite tomar decisiones críticas en tiempo real con total confianza.",
    person: "Carlos Mendoza",
    role: "Director de Operaciones",
  },
  {
    company: "ENERGÍA RENOVABLE S.A.",
    quote:
      "Estimamos que la solución reduce nuestro tiempo de respuesta ante incidencias en un 60%. Ahora podemos predecir fallos antes de que ocurran.",
    person: "Elena Vázquez",
    role: "CTO",
  },
  {
    company: "LOGÍSTICA MEDITERRÁNEA",
    quote:
      "Teníamos un gran volumen de datos pero sin capacidad de análisis. Con Arpano, en tres meses construimos lo que nos habría llevado tres años.",
    person: "Miguel Fernández",
    role: "Jefe de Innovación",
  },
  {
    company: "AUTOMÓVILES DEL SUR",
    quote:
      "Necesitábamos escalar nuestra capacidad de orquestación autónoma. Si dependiéramos solo de humanos, sería imposible gestionar 50 millones de decisiones diarias.",
    person: "Ana García",
    role: "VP de Manufactura",
  },
  {
    company: "FARMACÉUTICA NACIONAL",
    quote:
      "La trazabilidad completa de nuestra cadena de suministro nos ha permitido reducir pérdidas en un 45% y cumplir con todas las regulaciones europeas.",
    person: "Roberto Sánchez",
    role: "Director de Cadena de Suministro",
  },
  {
    company: "TELECOMUNICACIONES NORTE",
    quote:
      "No solo nos dicen qué está pasando, sino qué va a pasar. La capacidad predictiva ha transformado completamente nuestra gestión de infraestructura.",
    person: "Laura Martínez",
    role: "Directora de Infraestructura",
  },
];

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <article
      className={cn(
        "group relative shrink-0 w-[280px] sm:w-[320px] lg:w-[360px]",
        "bg-card text-card-foreground",
        "border border-border",
        "shadow-[0_10px_30px_oklch(0.25_0.02_60/0.08)]",
        "[clip-path:polygon(20px_0,100%_0,100%_100%,0_100%,0_20px)]",
        "overflow-hidden",
      )}
    >
      <div className="p-5 sm:p-6 flex flex-col h-full">
        <header className="mb-4">
          <div className="font-sans text-sm sm:text-base font-semibold tracking-tight text-foreground group-hover:-translate-y-1 transition-transform duration-300 ease-out">
            {testimonial.company}
          </div>
        </header>
        <p
          className={cn(
            "font-mono text-xs sm:text-sm leading-relaxed text-muted-foreground line-clamp-5",
            "transition-transform duration-300 ease-out",
            "group-hover:-translate-y-3",
          )}
        >
          "{testimonial.quote}"
        </p>

        {/* Person info - appears on hover */}
        <div
          className={cn(
            "mt-auto pt-4",
            "opacity-0 translate-y-3",
            "transition-all duration-300 ease-out",
            "group-hover:opacity-100 group-hover:translate-y-0",
          )}
        >
          <div className="font-sans text-sm font-medium text-foreground">
            {testimonial.person}
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {testimonial.role}
          </div>
        </div>
      </div>

      {/* Notch accent highlight */}
      <div
        className="pointer-events-none absolute left-0 top-0 h-[20px] w-[20px]"
        style={{
          background:
            "linear-gradient(135deg, transparent 50%, var(--primary) 50%)",
          opacity: 0.4,
        }}
      />
    </article>
  );
}

export function TestimonialsMarquee() {
  return (
    <section className="relative py-12 sm:py-16 lg:py-20 bg-background overflow-hidden">
      {/* Section header */}
      <div className="mx-auto max-w-6xl px-6 mb-8 sm:mb-10">
        <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
          Lo que dicen nuestros partners
        </h2>
        <p className="mt-2 font-mono text-sm text-muted-foreground max-w-[70ch]">
          Resultados medibles en entornos de decisión críticos.
        </p>
      </div>

      {/* Marquee container */}
      <div className="relative">
        {/* Edge fade gradients */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-linear-to-r from-background to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-linear-to-l from-background to-transparent z-10" />

        {/* Animated track */}
        <div
          className={cn(
            "marquee-track flex w-max gap-4 sm:gap-5 lg:gap-6 px-6",
            "[--marquee-duration:150s] sm:[--marquee-duration:140s] lg:[--marquee-duration:120s]",
          )}
        >
          {[...testimonials, ...testimonials].map((testimonial, idx) => (
            <TestimonialCard
              key={`${testimonial.company}-${idx}`}
              testimonial={testimonial}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
